import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  CustomInsightsReportFragment,
  useCustomInsightsReportsQuery,
} from "../../../graphql";

type SavedReportsContextValue = {
  reports: CustomInsightsReportFragment[];
  loading: boolean;
  addSavedReport: (report: CustomInsightsReportFragment) => void;
};

const SavedReportsContext = createContext<SavedReportsContextValue | null>(
  null
);

export function SavedReportsProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const { data, loading } = useCustomInsightsReportsQuery();
  const serverReports =
    data?.currentUser?.organization.customInsightsReports ?? [];
  const [sessionReports, setSessionReports] = useState<
    CustomInsightsReportFragment[]
  >([]);

  const reports = useMemo(() => {
    const serverIds = new Set(serverReports.map((r) => r.id));
    const newOnly = sessionReports.filter((r) => !serverIds.has(r.id));
    return [...serverReports, ...newOnly].sort((a, b) => {
      return a.createdAt < b.createdAt ? 1 : -1;
    });
  }, [serverReports, sessionReports]);

  const addSavedReport = useCallback((report: CustomInsightsReportFragment) => {
    setSessionReports((prev) => [...prev, report]);
  }, []);

  const value = useMemo(
    () => ({ reports, loading, addSavedReport }),
    [reports, loading, addSavedReport]
  );

  return (
    <SavedReportsContext.Provider value={value}>
      {children}
    </SavedReportsContext.Provider>
  );
}

export function useSavedReports(): SavedReportsContextValue {
  const ctx = useContext(SavedReportsContext);
  if (!ctx)
    throw new Error("useSavedReports must be used within SavedReportsProvider");
  return ctx;
}
